// 📁 src/lightversion/MarketPotentialLite.jsx

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "../utils/axiosInstance";
import { useLite } from "./context/LiteContext";
import { useNavigate } from "react-router-dom";
import LiteDayGate from "./LiteDayGate";
import LiteDayLayout from "./LiteDayLayout";
import ExperienceItem from "./components/ExperienceItem";
import LiteLearningBlock from "./components/LiteLearningBlock";

/* --------------------------------------------------
   🔐 CONSTANTS
-------------------------------------------------- */

const RESUME_KEY = "lite_resume_step";
const REFLECTION_KEY = "lite_market_reflection";
const EXECUTE_AFTER_RESUME_KEY = "lite_market_execute_after_resume";
const LITE_REG_CONFIRM_KEY = "lite_registration_confirmed";

const MarketPotentialLite = () => {
  const navigate = useNavigate();
  const hasAutoExecuted = useRef(false); // 🔑 critical guard

  const { 
  liteJourney,
  markDayComplete,
  entryDecided,
  loading: liteLoading
  } = useLite();

  const completedDays = liteJourney?.completedDays || [];
  const purposeStatement = liteJourney?.purpose?.statement;

  const [reflection, setReflection] = useState("");
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [isLiteRegistered, setIsLiteRegistered] = useState(false);

      useEffect(() => {
      const checkRegistration = () => {
        const flag =
          localStorage.getItem(LITE_REG_CONFIRM_KEY) === "true";
        setIsLiteRegistered(flag);
      };

      checkRegistration();

      // 🔁 also re-check when user comes back
      window.addEventListener("focus", checkRegistration);

      return () => {
        window.removeEventListener("focus", checkRegistration);
      };
    }, [navigate]);

  /* =====================================================
     🔒 ENTRY DECISION GUARD
  ===================================================== */
  useEffect(() => {

  if (liteLoading) return;

  if (!entryDecided && window.location.pathname !== "/lite/welcome") {
    navigate("/lite/welcome",{replace:true});
  }

  }, [entryDecided, liteLoading, navigate]);

  /* =====================================================
     🔁 DAY ORDER GUARDS
  ===================================================== */
  useEffect(() => {
    if (!completedDays.includes(1)) return navigate("/lite/mentor", { replace: true });
    if (!completedDays.includes(2)) return navigate("/lite/purpose", { replace: true });
    if (!completedDays.includes(3)) return navigate("/lite/idea", { replace: true });
    if (!completedDays.includes(4)) return navigate("/lite/direction-confirm", { replace: true });
    if (completedDays.includes(5)) navigate("/lite/learning", { replace: true });
  }, [completedDays, navigate]);

  /* =====================================================
     🔁 RESTORE REFLECTION
  ===================================================== */
  useEffect(() => {
    const saved = localStorage.getItem(REFLECTION_KEY);
    if (saved && !reflection) {
      setReflection(saved);
    }
  }, [reflection]);

  /* =====================================================
     🧠 EXPLORE MARKET REALITY
  ===================================================== */
  const handleExplore = async () => {
    setError("");

    if (!reflection.trim()) {
      setError("Pause for a moment and add one reflection before continuing.");
      return;
    }

    if (!isLiteRegistered) {
      localStorage.setItem(REFLECTION_KEY, reflection);
      localStorage.setItem(RESUME_KEY, "market-potential");
      localStorage.setItem(EXECUTE_AFTER_RESUME_KEY, "true");
      navigate("/lite/registration-gate");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post("/lite/market-potential", {
        purpose: purposeStatement,
        reflection,
        mode: "lite-relevance-awareness",
      });
      setInsight(res.data);
    } catch (e) {
      setError("We couldn’t generate insights right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     🔁 AUTO-RESUME (FIXED)
  ===================================================== */
  useEffect(() => {
    const resumeStep = localStorage.getItem(RESUME_KEY);
    const shouldExecute =
      localStorage.getItem(EXECUTE_AFTER_RESUME_KEY) === "true";

    if (
      isLiteRegistered &&
      resumeStep === "market-potential" &&
      reflection &&
      !insight &&
      shouldExecute &&
      !hasAutoExecuted.current
    ) {
      hasAutoExecuted.current = true;

      localStorage.removeItem(RESUME_KEY);
      localStorage.removeItem(EXECUTE_AFTER_RESUME_KEY);

      handleExplore();
    }
  }, [isLiteRegistered, reflection, insight]);

  /* =====================================================
     ✅ COMPLETE DAY 5
  ===================================================== */
  const handleCompleteDay = () => {
    markDayComplete();
    navigate("/lite/learning");
  };

  return (
    <LiteDayGate allow={["USER_VALIDATION", "MARKET_REALITY"]}>
      <LiteDayLayout
        day={5}
        allowAction={!!insight}
        onComplete={handleCompleteDay}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
            Does This Problem Matter at Scale?
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Today is about awareness — not validation.
          </Typography>

          <Stack spacing={4} sx={{ mb: 6 }}>
            <ExperienceItem icon="🌍" title="Look beyond yourself" text="Every great startup begins with a spark, but it thrives when that spark lights up others’ lives.Ask yourself: Does this problem truly matter to people beyond me? The most powerful ventures solve meaningful challenges that ripple across communities.When you build for others, you build something that lasts." />
            <ExperienceItem icon="🧠" title="Understand persistence" text="Every unsolved problem carries a story of resistance.Ask yourself: Why hasn’t this been cracked yet? Barriers reveal opportunities—hidden gaps waiting for bold founders to step in. Persistence isn’t just about pushing harder; it’s about uncovering what others overlooked." />
            <ExperienceItem icon="🌱" title="Build relevance awareness" text="Clarity is the compass that saves years of wandering.Before you pour energy into building, ask: Does this truly matter right now? The sharpest founders cut through noise to focus on what counts.Relevance isn’t optional—it’s the difference between wasted effort and lasting impact." />
          </Stack>

          <LiteLearningBlock day={5} />

          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle2">Your purpose</Typography>

                <Typography sx={{ fontStyle: "italic", color: "text.secondary" }}>
                  “{purposeStatement || "Not set yet"}”
                </Typography>

                <Divider />

                <TextField
                  label="Your reflection"
                  multiline
                  minRows={3}
                  fullWidth
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                />

                {error && <Typography color="error">{error}</Typography>}

                <Button
                  variant="contained"
                  size="large"
                  disabled={loading}
                  onClick={handleExplore}
                >
                  {loading ? "Exploring…" : "Understand real-world relevance"}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {insight && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography><strong>Who is affected:</strong> {insight.whoIsAffected}</Typography>
                    <Typography><strong>Why it persists:</strong> {insight.whyGrowing}</Typography>
                    <Typography><strong>Why it matters:</strong> {insight.whyItMatters}</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </Box>
      </LiteDayLayout>
    </LiteDayGate>
  );
};

export default MarketPotentialLite;
